import { IconBrandsFacebook } from '@doltech/icons/IconBrandsFacebook';
import { imgProxyLoader } from '@doltech/utils/lib/image-loader';
import Image from 'next/image';
import * as React from 'react';
import styled from 'styled-components';
import { fromScreen } from '@doltech/core/lib/responsive/responsive.util';

import { BlogPostAuthor } from '../../../Blog/BlogHeader';
import { colorsV2 } from '../../../colors-v2';
import { Box } from '../../../Common/BoxV2';
import { Typography } from '../../../DesignSystemV2/Typography';
import { DolFacebookShareButton } from '../../../Social/DolFacebookShareButton';
import { Separator } from '../Content/Separator';
import { LandingLayoutWithSEOHeaderContext } from '../LandingLayoutWithSEOHeaderContext';
import { formatDateStringForSEO } from '@doltech/utils/lib/date';

interface AuthorInfoProps {
  id?: string;
  author: BlogPostAuthor;
  publishedDate: Date;
  readTimeInMinutes?: number;
  seoUrl: string;
  withBorder?: boolean;
  withActions?: boolean;
  pageId: string;
}

const Main = styled.div<{ withBorder }>`
  .author-container {
    border-top: ${(p) => (p.withBorder ? `1px solid ${colorsV2.black10}` : 'unset')};
    border-bottom: ${(p) => (p.withBorder ? `1px solid ${colorsV2.black10}` : 'unset')};
    padding: 16px 0;
    .actions-container {
      display: none;
      grid-template-columns: max-content max-content max-content;
      align-items: center;
      gap: 8px;
      justify-self: start;
      ${fromScreen(768)} {
        display: grid;
        justify-self: end;
      }
    }
  }
  .author-n-social {
    display: grid;
    grid-template-columns: unset;
    grid-template-rows: auto auto;
    ${fromScreen(768)} {
      grid-template-columns: auto auto;
      align-items: center;
    }
  }

  .post-info-row {
    display: flex;
    align-items: center;
    .author {
      display: flex;
      align-items: center;
      .author-image {
        width: 32px;
        height: 32px;
        position: relative;
        border-radius: 50%;
        margin-right: 8px;
      }
    }

    .author-info {
      display: flex;
      align-items: flex-start;
      flex-direction: column;
      .author-name {
        font-size: 16px;
        font-weight: 400;
        line-height: 24px;
        color: ${colorsV2.green100};
      }

      ${fromScreen(768)} {
        align-items: center;
        flex-direction: row;
      }
    }

    .publish-n-read-time-row {
      display: flex;
      align-items: center;
      .published-date,
      .reading-time {
        color: ${colorsV2.black80};
      }
      .dol-separator {
        margin-left: 8px;
        margin-right: 8px;
      }
    }
  }
  .social-container {
    button,
    button:hover {
      background-color: unset;
    }
    .fb-share {
      display: flex;
    }
  }
  .action-icon {
    font-size: 24px;
    line-height: 0;
  }
  &.fb-share {
    .action-icon {
      font-size: 20px;
    }
  }
  .btn-action {
    background-color: transparent;
    border: none;
    cursor: pointer;

    padding: 8px 16px;
    display: grid;
    grid-auto-flow: column;
    align-items: center;
    gap: 12px;
    grid-template-columns: max-content;
    transition: all 0.2s ease;
    &:hover {
      transform: scale(1.02);
    }
    &:active {
      transform: scale(0.98);
    }
    .action-icon {
      font-size: 24px;
      line-height: 0;
    }
    &.fb-share {
      .action-icon {
        font-size: 20px;
      }
    }
  }
`;

export const AuthorInfoPdf = (props: Partial<AuthorInfoProps>) => {
  const { id, author = {}, publishedDate, readTimeInMinutes, withBorder } = props;
  const { name: authorName = '...', avatarUrl } = author;

  const ExtraInfo = () => (
    <div className="publish-n-read-time-row">
      {publishedDate && (
        <>
          <Separator variant="dot" />
          <div className="published-date">
            <Typography.Text variant="regular/12-20">
              {formatDateStringForSEO(publishedDate)}
            </Typography.Text>
          </div>
        </>
      )}

      {readTimeInMinutes > 0 && (
        <>
          <Separator variant="dot" />{' '}
          <div className="reading-time">
            <Typography.Text variant="regular/12-20">
              {readTimeInMinutes}
              min read
            </Typography.Text>
          </div>
        </>
      )}
    </div>
  );

  return (
    <Main id={id} className="author-info" withBorder={withBorder}>
      <Box className="author-container">
        <div className="author-n-social">
          <div className="post-info-row">
            <div className="author">
              <div className="author-image">
                <Image
                  objectFit="cover"
                  objectPosition="center"
                  loader={imgProxyLoader}
                  layout="fill"
                  src={avatarUrl}
                  alt={authorName}
                />
              </div>
            </div>
            <div className="author-info">
              <Typography.Text className="author-name">{authorName}</Typography.Text>
              <ExtraInfo />
            </div>
          </div>
        </div>
      </Box>
    </Main>
  );
};

const ExtraInfo = ({ publishedDate, readTimeInMinutes }) => (
  <div className="publish-n-read-time-row">
    {publishedDate && (
      <>
        <Separator display={['none', 'grid', 'grid']} variant="dot" />
        <div className="published-date">
          <Typography.Text variant="regular/12-20">
            {formatDateStringForSEO(publishedDate)}
          </Typography.Text>
        </div>
      </>
    )}

    {readTimeInMinutes > 0 && (
      <>
        <Separator variant="dot" />{' '}
        <div className="reading-time">
          <Typography.Text variant="regular/12-20">
            {readTimeInMinutes}
            min read
          </Typography.Text>
        </div>
      </>
    )}
  </div>
);

export const AuthorInfo = (props: AuthorInfoProps) => {
  const {
    id,
    author = {},
    publishedDate,
    seoUrl,
    readTimeInMinutes,
    withBorder,
    withActions,
    pageId,
  } = props;
  const { name: authorName = '...', avatarUrl } = author;
  const { socials } = React.useContext(LandingLayoutWithSEOHeaderContext);
  return (
    <Main id={id} className="author-info" withBorder={withBorder}>
      <Box className="author-container">
        <div className="author-n-social">
          <div className="post-info-row">
            <div className="author">
              <div className="author-image">
                <Image
                  objectFit="cover"
                  objectPosition="center"
                  loader={imgProxyLoader}
                  layout="fill"
                  src={avatarUrl}
                  alt={authorName}
                />
              </div>
            </div>
            <div className="author-info">
              <Typography.Text className="author-name">{authorName}</Typography.Text>
              <ExtraInfo publishedDate={publishedDate} readTimeInMinutes={readTimeInMinutes} />
            </div>
          </div>
          {withActions && (
            <div className="actions-container">
              <DolFacebookShareButton
                pageId={pageId}
                className="btn-action fb-share"
                key="facebook-share"
                url={seoUrl}
              >
                <div className="action-icon">
                  <IconBrandsFacebook />
                </div>
              </DolFacebookShareButton>
              {socials?.length > 0 && (
                <>
                  <Separator height="60%" variant="vertical" />
                  <div className="social-container">{socials ? [...socials] : null}</div>
                </>
              )}
            </div>
          )}
        </div>
      </Box>
    </Main>
  );
};
